// Browser-native, on-device APIs only. Do not add cloud/network fallbacks here.
// https://developer.chrome.com/docs/ai/translator-api
// https://developer.chrome.com/docs/ai/language-detection
export const translationLanguages = [
  {code: 'it', label: 'Italiano'},
  {code: 'en', label: 'English'},
  {code: 'de', label: 'Deutsch'},
  {code: 'fr', label: 'Français'},
  {code: 'es', label: 'Español'},
] as const;

export type TranslationLanguage = typeof translationLanguages[number]['code'];
export type TranslationSourceLanguage = TranslationLanguage | 'auto';
export type TranslationText = {id: string; label: string; text: string};
export type TranslatedText = TranslationText & {original: string};
export type TranslationResult = {sourceLanguage: string; targetLanguage: TranslationLanguage; texts: TranslatedText[]; unchanged: boolean};
export type TranslationProgress = {phase: 'detecting' | 'downloading' | 'translating'; progress?: number};
type Availability = 'available' | 'downloadable' | 'downloading' | 'unavailable';
type Monitor = {addEventListener(type: 'downloadprogress', listener: (event: {loaded: number}) => void): void};
type CreateOptions = {signal?: AbortSignal; monitor?: (monitor: Monitor) => void};
type NativeTranslator = {translate(text: string, options?: {signal?: AbortSignal}): Promise<string>; destroy(): void};
type NativeDetector = {detect(text: string, options?: {signal?: AbortSignal}): Promise<{detectedLanguage: string; confidence: number}[]>; destroy(): void};
export type TranslationEnvironment = {
  isSecureContext?: boolean;
  navigator?: {userActivation?: {isActive: boolean}};
  Translator?: {
    availability(options: {sourceLanguage: string; targetLanguage: string}): Promise<Availability>;
    create(options: CreateOptions & {sourceLanguage: string; targetLanguage: string}): Promise<NativeTranslator>;
  };
  LanguageDetector?: {availability(): Promise<Availability>; create(options: CreateOptions): Promise<NativeDetector>};
};

export const MAX_TRANSLATION_TEXT_LENGTH = 6000;
export const MAX_TRANSLATION_TOTAL_LENGTH = 12000;
const MAX_FIELDS = 6;
const MAX_CACHE_ENTRIES = 24;
const MAX_CACHE_CHARACTERS = 100000;
const MAX_MODELS = 3;
const MAX_TRANSLATION_OUTPUT_LENGTH = 30000;
const MIN_DETECTION_CONFIDENCE = 0.75;

export class OnDeviceTranslationError extends Error {
  constructor(public readonly code: 'unsupported' | 'source-required' | 'unavailable' | 'activation-required' | 'too-long' | 'invalid' | 'integrity' | 'failed', message: string) {
    super(message);
    this.name = 'OnDeviceTranslationError';
  }
}

function defaultEnvironment(): TranslationEnvironment {
  return globalThis as unknown as TranslationEnvironment;
}

export function onDeviceTranslationSupport(environment: TranslationEnvironment = defaultEnvironment()) {
  const supported = environment.isSecureContext === true && typeof environment.Translator?.create === 'function' && typeof environment.Translator?.availability === 'function';
  const canDetectLanguage = supported && typeof environment.LanguageDetector?.create === 'function' && typeof environment.LanguageDetector?.availability === 'function';
  return {
    supported,
    canDetectLanguage,
    message: supported ? '' : 'Traduzione locale non disponibile in questo browser. Usa Chrome desktop aggiornato su una connessione HTTPS; su mobile o nei browser non compatibili resta visibile il testo originale.',
  };
}

export function validateTranslationTexts(texts: readonly TranslationText[]): TranslationText[] {
  if (texts.length > MAX_FIELDS || new Set(texts.map(text => text.id)).size !== texts.length || texts.some(text => !text.id || !text.label || typeof text.text !== 'string')) {
    throw new OnDeviceTranslationError('invalid', 'Contenuti da tradurre non validi.');
  }
  if (texts.some(text => text.text.length > MAX_TRANSLATION_TEXT_LENGTH) || texts.reduce((total, text) => total + text.text.length, 0) > MAX_TRANSLATION_TOTAL_LENGTH) {
    throw new OnDeviceTranslationError('too-long', `Il testo supera il limite della traduzione locale (${MAX_TRANSLATION_TEXT_LENGTH} caratteri per campo). L’originale non è stato modificato.`);
  }
  return texts.filter(text => text.text.trim().length > 0).map(text => ({...text}));
}

function assertNotAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('Traduzione annullata.', 'AbortError');
}

function abortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new DOMException('Traduzione annullata.', 'AbortError'));
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(new DOMException('Traduzione annullata.', 'AbortError'));
    signal.addEventListener('abort', abort, {once: true});
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

function destroy(model: NativeTranslator | NativeDetector) {
  try { model.destroy(); } catch { /* A destroyed browser model needs no further cleanup. */ }
}

function normalLanguage(code: string) {
  if (!/^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i.test(code) || code.toLowerCase() === 'und') return '';
  return code.toLowerCase();
}

function downloadMonitor(onProgress?: (status: TranslationProgress) => void, signal?: AbortSignal): CreateOptions['monitor'] {
  return monitor => monitor.addEventListener('downloadprogress', event => {
    if (!signal?.aborted && Number.isFinite(event.loaded)) onProgress?.({phase: 'downloading', progress: Math.max(0, Math.min(1, event.loaded))});
  });
}

/** Keep technical names and literal numbers byte-for-byte unchanged, or reject the output. */
export function protectTranslationText(text: string, protectedTerms: readonly string[] = []) {
  if (text.includes('__GRUVO_KEEP_')) throw new OnDeviceTranslationError('invalid', 'Questo testo non può essere tradotto automaticamente.');
  const terms = [...new Set(protectedTerms.map(term => term.trim()).filter(term => term.length > 1 && term.length <= 160))].slice(0, 20).sort((a, b) => b.length - a.length);
  const escapedTerms = terms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  // Protect numeric specifications, serial fragments, prices and mixed alphanumeric model names.
  const numericOrModel = '[\\p{L}\\p{N}]*\\d[\\p{L}\\p{N}]*(?:[.,/–−-]\\d+[\\p{L}\\p{N}]*)*(?:\\s?[%°€$])?';
  const pattern = new RegExp([...escapedTerms, numericOrModel].join('|'), 'gu');
  const values: string[] = [];
  const masked = text.replace(pattern, match => {
    const index = values.push(match) - 1;
    return `__GRUVO_KEEP_${index}__`;
  });
  return {
    text: masked,
    restore(translated: string) {
      if (typeof translated !== 'string' || translated.length > MAX_TRANSLATION_OUTPUT_LENGTH || !translated.trim()) throw new OnDeviceTranslationError('failed', 'Il browser non ha restituito una traduzione valida.');
      let restored = translated;
      for (let index = 0; index < values.length; index += 1) {
        const placeholder = `__GRUVO_KEEP_${index}__`;
        if (restored.split(placeholder).length !== 2) throw new OnDeviceTranslationError('integrity', 'La traduzione non ha conservato un modello o un valore numerico. Per sicurezza viene mantenuto l’originale.');
        restored = restored.replace(placeholder, () => values[index]);
      }
      if (restored.includes('__GRUVO_KEEP_')) throw new OnDeviceTranslationError('integrity', 'La traduzione non ha conservato i dati tecnici. Viene mantenuto l’originale.');
      return restored;
    },
  };
}

export function translationErrorMessage(error: unknown) {
  if (error instanceof OnDeviceTranslationError) return error.message;
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError') return 'Il browser richiede un nuovo clic per preparare la traduzione, oppure la funzione è bloccata dalle sue impostazioni. Premi Riprova; se persiste, usa l’originale.';
  if (name === 'NotSupportedError') return 'Questa combinazione di lingue non è supportata dal browser. Scegli un’altra lingua o mostra l’originale.';
  if (name === 'QuotaExceededError') return 'Il browser non può tradurre questo testo per limiti di memoria o lunghezza. L’originale resta disponibile.';
  return 'Traduzione locale non riuscita. Controlla la disponibilità dei pacchetti lingua e riprova; l’originale non è stato modificato.';
}

/** A short-lived component-owned session. Text/output caches are memory-only and bounded. */
export class OnDeviceTranslationClient {
  private detector: NativeDetector | undefined;
  private translators = new Map<string, NativeTranslator>();
  private detections = new Map<string, string>();
  private cache = new Map<string, TranslationResult>();
  private creationControllers = new Set<AbortController>();
  private disposed = false;

  constructor(private readonly environment: TranslationEnvironment = defaultEnvironment()) {}

  private checkActivation(availability: Availability) {
    if (availability === 'unavailable') throw new OnDeviceTranslationError('unavailable', 'Questa combinazione di lingue o il modello locale non è disponibile nel browser. L’originale resta visibile.');
    if (availability !== 'available' && this.environment.navigator?.userActivation?.isActive === false) {
      throw new OnDeviceTranslationError('activation-required', 'Per scaricare il pacchetto lingua serve un nuovo clic. Premi Riprova: il testo resta sul tuo dispositivo.');
    }
  }

  private async createModel<T extends NativeTranslator | NativeDetector>(factory: (options: CreateOptions) => Promise<T>, signal?: AbortSignal, onProgress?: (status: TranslationProgress) => void): Promise<T> {
    assertNotAborted(signal);
    const creation = new AbortController();
    const abort = () => creation.abort();
    this.creationControllers.add(creation);
    signal?.addEventListener('abort', abort, {once: true});
    try {
      const pending = factory({signal: creation.signal, monitor: downloadMonitor(onProgress, signal)});
      // Even a non-compliant implementation that resolves after cancellation is cleaned up.
      void pending.then(model => { if (creation.signal.aborted || this.disposed) destroy(model); }, () => {});
      const model = await abortable(pending, signal);
      assertNotAborted(signal);
      if (this.disposed) { destroy(model); throw new DOMException('Traduzione annullata.', 'AbortError'); }
      return model;
    } finally {
      signal?.removeEventListener('abort', abort);
      this.creationControllers.delete(creation);
    }
  }

  private async detect(text: string, signal?: AbortSignal, onProgress?: (status: TranslationProgress) => void) {
    const cached = this.detections.get(text);
    if (cached) return cached;
    const api = this.environment.LanguageDetector;
    if (!api) throw new OnDeviceTranslationError('source-required', 'Il browser non può rilevare la lingua. Seleziona la lingua originale prima di tradurre.');
    if (text.replace(/[^\p{L}]/gu, '').length < 20) throw new OnDeviceTranslationError('source-required', 'Il testo è troppo breve per rilevare la lingua con affidabilità. Seleziona la lingua originale.');
    onProgress?.({phase: 'detecting'});
    if (!this.detector) {
      const availability = await abortable(api.availability(), signal);
      this.checkActivation(availability);
      if (availability !== 'available') onProgress?.({phase: 'downloading'});
      this.detector = await this.createModel(options => api.create(options), signal, onProgress);
    }
    const results = await abortable(this.detector.detect(text, {signal}), signal);
    assertNotAborted(signal);
    const best = [...results].filter(result => Number.isFinite(result.confidence)).sort((a, b) => b.confidence - a.confidence)[0];
    const language = best && normalLanguage(best.detectedLanguage);
    if (!language || best.confidence < MIN_DETECTION_CONFIDENCE) throw new OnDeviceTranslationError('source-required', 'La lingua originale non è stata riconosciuta con sufficiente sicurezza. Selezionala prima di tradurre.');
    if (this.detections.size >= 8) this.detections.delete(this.detections.keys().next().value!);
    this.detections.set(text, language);
    return language;
  }

  async translate({texts, targetLanguage, sourceLanguage = 'auto', protectedTerms = [], signal, onProgress}: {
    texts: readonly TranslationText[];
    targetLanguage: TranslationLanguage;
    sourceLanguage?: TranslationSourceLanguage;
    protectedTerms?: readonly string[];
    signal?: AbortSignal;
    onProgress?: (status: TranslationProgress) => void;
  }): Promise<TranslationResult> {
    assertNotAborted(signal);
    if (this.disposed) throw new DOMException('Traduzione annullata.', 'AbortError');
    if (!onDeviceTranslationSupport(this.environment).supported) throw new OnDeviceTranslationError('unsupported', onDeviceTranslationSupport(this.environment).message);
    if (!translationLanguages.some(language => language.code === targetLanguage) || (sourceLanguage !== 'auto' && !translationLanguages.some(language => language.code === sourceLanguage))) throw new OnDeviceTranslationError('invalid', 'Lingua non valida.');
    const fields = validateTranslationTexts(texts);
    if (!fields.length) throw new OnDeviceTranslationError('invalid', 'Non c’è testo pubblico da tradurre.');
    const detectedLanguage = sourceLanguage === 'auto' ? await this.detect(fields.map(field => field.text).join('\n\n'), signal, onProgress) : sourceLanguage;
    assertNotAborted(signal);
    if (detectedLanguage.split('-')[0] === targetLanguage) {
      return {sourceLanguage: detectedLanguage, targetLanguage, texts: fields.map(field => ({...field, original: field.text})), unchanged: true};
    }
    const key = JSON.stringify([detectedLanguage, targetLanguage, fields, protectedTerms]);
    const cached = this.cache.get(key);
    if (cached) {
      this.cache.delete(key);
      this.cache.set(key, cached);
      return {...cached, texts: cached.texts.map(field => ({...field}))};
    }
    const pair = `${detectedLanguage}:${targetLanguage}`;
    let translator = this.translators.get(pair);
    if (!translator) {
      const api = this.environment.Translator!;
      const availability = await abortable(api.availability({sourceLanguage: detectedLanguage, targetLanguage}), signal);
      assertNotAborted(signal);
      this.checkActivation(availability);
      if (availability !== 'available') onProgress?.({phase: 'downloading'});
      translator = await this.createModel(options => api.create({...options, sourceLanguage: detectedLanguage, targetLanguage}), signal, onProgress);
      if (this.translators.size >= MAX_MODELS) {
        const oldest = this.translators.keys().next().value!;
        destroy(this.translators.get(oldest)!);
        this.translators.delete(oldest);
      }
      this.translators.set(pair, translator);
    }
    onProgress?.({phase: 'translating'});
    const translatedFields: TranslatedText[] = [];
    // Native translation is sequential; keep fields separate instead of parsing model delimiters.
    for (const field of fields) {
      assertNotAborted(signal);
      const protectedText = protectTranslationText(field.text, protectedTerms);
      const translated = await abortable(translator.translate(protectedText.text, {signal}), signal);
      assertNotAborted(signal);
      translatedFields.push({...field, original: field.text, text: protectedText.restore(translated)});
    }
    const result = {sourceLanguage: detectedLanguage, targetLanguage, texts: translatedFields, unchanged: false};
    assertNotAborted(signal);
    this.cache.set(key, result);
    while (this.cache.size > MAX_CACHE_ENTRIES || [...this.cache.entries()].reduce((size, [cacheKey, item]) => size + cacheKey.length + item.texts.reduce((length, field) => length + field.text.length + field.original.length, 0), 0) > MAX_CACHE_CHARACTERS) {
      this.cache.delete(this.cache.keys().next().value!);
    }
    return {...result, texts: result.texts.map(field => ({...field}))};
  }

  dispose() {
    this.disposed = true;
    for (const controller of this.creationControllers) controller.abort();
    this.creationControllers.clear();
    if (this.detector) destroy(this.detector);
    for (const model of this.translators.values()) destroy(model);
    this.detector = undefined;
    this.translators.clear();
    this.detections.clear();
    this.cache.clear();
  }
}
